'use client'

import { useTranslations } from 'next-intl'
import { useEffect, useMemo, useRef } from 'react'

import EmptyIllustration from '@/assets/icons/tasks-feed/empty-illustration.svg'
import { useAvailableTasksInfinite } from '@/features/tasks/hooks/useAvailableTasksInfinite'
import {
  getTaskCardCtaLabelKey,
  getTaskCardDetailPath
} from '@/features/tasks/taskCardCta'
import {
  currentAccountType,
  type FilterName,
  type SortName,
  track,
  type ViewState
} from '@/lib/productAnalytics'

import { TASK_FEED_TYPE_ICON } from '../TaskFeedCard/taskFeedCard.constants'
import { TaskFeedRowCard } from '../TaskFeedCard/TaskFeedRowCard'
import { trackTaskFeedCardClick } from '../TaskFeedCard/trackTaskFeedCardClick'
import TasksComingSoon from '../TasksComingSoon'
import TasksEmptyState from '../TasksEmptyState'
import TasksFeedGrid, {
  TasksFeedGridError,
  TasksFeedGridLoading
} from '../TasksFeedGrid'
import TasksFeedNav from '../TasksFeedNav'
import { resolveFeedPlatforms } from '../TasksFeedNav/feedPlatforms'
import {
  EXPLORE_FEED_SORTS,
  SOON_FEED_TYPES,
  type TasksFeedChip,
  type TasksFeedSortId
} from '../TasksFeedNav/tasksFeedNav.constants'
import {
  PLATFORMS_FILTER_ID,
  usePlatformsFilter
} from '../TasksFeedNav/useFeedFilters'
import {
  TASKS_FILTER_SEGMENTS,
  type TasksFilterSegmentId
} from '../TasksFilterBar/tasksFilterBar.constants'
import type { TasksFeedControls } from '../TasksListingSection'

const SORT_NAME: Record<TasksFeedSortId, SortName> = {
  default: 'default',
  rewardDesc: 'reward_high_low',
  rewardAsc: 'reward_low_high',
  deadlineAsc: 'nearest_deadline',
  newest: 'newest_first',
  spotsLeftAsc: 'fewest_spots_left'
}

const FILTER_NAME: Partial<Record<TasksFilterSegmentId, FilterName>> = {
  all: 'all',
  watch: 'watch',
  share: 'share',
  action: 'action'
}

export type TasksExploreFeedProps = {
  controls: TasksFeedControls<TasksFilterSegmentId>
}

export default function TasksExploreFeed({ controls }: TasksExploreFeedProps) {
  const { chip: segment, sort, onPlatformsChange } = controls
  const t = useTranslations('tasksPage.feedNav')
  const tCard = useTranslations('tasksPage.taskCard')
  const tFeed = useTranslations('tasksPage.feed')
  const {
    query,
    countQuery,
    platforms: offered,
    platformsFetched,
    availableRows,
    comingSoonRows,
    handleLoadMore,
    isFetchingNextPage
  } = useAvailableTasksInfinite(sort, segment, controls.platforms)

  // The API filters and orders the lists; the picked platforms only need
  // checking against the Platforms menu once the lists answered.
  const platforms = platformsFetched
    ? resolveFeedPlatforms(controls.platforms, offered)
    : controls.platforms
  // A picked platform left the menu (e.g. its last task was taken, or the
  // type changed): drop it and load the lists without it.
  const platformGone = platforms !== controls.platforms
  useEffect(() => {
    if (platformGone) {
      onPlatformsChange(platforms)
    }
  }, [onPlatformsChange, platformGone, platforms])

  const platformsFilter = usePlatformsFilter(
    offered,
    platforms,
    onPlatformsChange
  )
  const filtered = segment !== 'all' || platforms.length > 0

  const chips = useMemo<TasksFeedChip<TasksFilterSegmentId>[]>(
    () =>
      TASKS_FILTER_SEGMENTS.map(id => {
        if (id === 'all') {
          return { id, label: t('allTasks') }
        }
        const Icon = TASK_FEED_TYPE_ICON[id]
        return {
          id,
          label: tCard(getTaskCardCtaLabelKey(id)),
          icon: <Icon />,
          soon: SOON_FEED_TYPES.includes(id)
        }
      }),
    [t, tCard]
  )

  const rows = availableRows

  const showInitialLoading = !query.isFetched || platformGone
  const listViewState: ViewState | null = showInitialLoading
    ? null
    : query.isError
    ? 'error'
    : rows.length > 0
    ? 'data'
    : 'empty'
  // Every list view counts; only repeats of the same combination are skipped,
  // so loading more tasks does not re-fire.
  const lastListViewRef = useRef<string | null>(null)

  useEffect(() => {
    const filterName = FILTER_NAME[segment]
    if (!listViewState || !filterName) {
      return
    }
    const key = `${listViewState}|${filterName}|${sort}|${platforms.join(',')}`
    if (lastListViewRef.current === key) {
      return
    }
    lastListViewRef.current = key
    track({
      name: 'tasks_list_viewed',
      props: {
        account_type: currentAccountType(),
        view_state: listViewState,
        filter_name: filterName,
        sort_name: SORT_NAME[sort],
        visible_task_count: String(listViewState === 'data' ? rows.length : 0)
      }
    })
  }, [listViewState, platforms, rows.length, segment, sort])

  const hasMore = Boolean(query.hasNextPage)

  const navBar = (
    <TasksFeedNav
      view="explore"
      onViewChange={controls.onViewChange}
      chips={chips}
      selectedChip={segment}
      onChipChange={controls.onChipChange}
      chipsAriaLabel={t('typeAriaLabel')}
      filters={[platformsFilter]}
      sort={sort}
      sortOptions={EXPLORE_FEED_SORTS}
      onSortChange={controls.onSortChange}
      onClearAll={filtered ? controls.onClearAll : undefined}
      countPending={pending => countQuery(pending[PLATFORMS_FILTER_ID] ?? [])}
    />
  )

  if (showInitialLoading) {
    return (
      <>
        {navBar}
        <TasksFeedGridLoading />
      </>
    )
  }

  if (query.isError) {
    return (
      <>
        {navBar}
        <TasksFeedGridError
          onRetry={() => {
            void query.refetch()
          }}
        />
      </>
    )
  }

  const empty =
    rows.length > 0 ? undefined : !filtered ? (
      comingSoonRows.length > 0 ? null : (
        <TasksEmptyState
          icon={<EmptyIllustration />}
          title={tCard('emptyListTitle')}
          description={tCard('emptyListDescription')}
        />
      )
    ) : (
      <TasksEmptyState
        icon={<EmptyIllustration />}
        title={tFeed('emptyFilteredTitle')}
        description={tFeed('emptyFilteredDescription')}
      />
    )

  return (
    <>
      {navBar}
      <TasksFeedGrid
        empty={empty}
        hasMore={hasMore}
        isLoadingMore={isFetchingNextPage}
        onLoadMore={handleLoadMore}
      >
        {rows.map(row => {
          const href = getTaskCardDetailPath(row)
          if (!href) {
            return null
          }
          return (
            <TaskFeedRowCard
              key={row.id}
              row={row}
              href={href}
              onClick={() => {
                trackTaskFeedCardClick(row)
              }}
            />
          )
        })}
      </TasksFeedGrid>
      {comingSoonRows.length > 0 ? (
        <TasksComingSoon rows={comingSoonRows} />
      ) : null}
    </>
  )
}
